/**
 * The join-failure classifier, ported VERBATIM from the app's
 * `src/Utils/joinTournamentFailure.ts` (which the old screen imports at L32-34
 * and `SocketProvider`'s `GamePlayStore.joinFailure` bridge imports too).
 *
 * It lives in the shared core because the reducer needs it and the core may not
 * import an app path — but the tables below are the same words, so the two
 * copies cannot classify one message differently. If the old file's tables
 * change, change these with them.
 *
 * ── why the classification exists ──
 * The server sends two distinct rejections that BOTH contain "already":
 *   - "Already registered in an overlapping tournament (X)." — a genuine
 *     rejection; the user must see the failure modal;
 *   - "Player is already registered in this tournament." — a harmless
 *     duplicate join, which the UI treats like a success.
 * A naive substring check conflates them.
 */

export type JoinFailureKind = 'overlapping' | 'already' | 'other';

export function classifyJoinFailureMessage(
  message?: string | null,
): JoinFailureKind {
  const lower = message?.toLowerCase?.() ?? '';
  if (lower.includes('overlapping')) {
    return 'overlapping';
  }
  if (lower.includes('already')) {
    return 'already';
  }
  return 'other';
}

/**
 * The rejected join, in the `joinTnrSuccess` vocabulary `TournamentPlay` reads:
 *   1 = show JoinFailureModal with the server's message
 *   2 = success-like (a harmless duplicate join) — no modal
 */
export function resolveJoinTnrSuccess(message?: string | null): 1 | 2 {
  return classifyJoinFailureMessage(message) === 'already' ? 2 : 1;
}

export type JoinConflict = {id: number; name?: string};

/**
 * Only STRUCTURED conflict data is trusted. Without an id there is nothing to
 * withdraw from, and matching on the NAME is unsafe because tournament names
 * repeat weekly. The backend rollout may predate `conflict_id`, so this returns
 * null (never throws) for any older payload shape.
 */
export function extractJoinConflict(
  payload?: {
    error_code?: string;
    conflict_id?: number | string;
    conflict_name?: string;
  } | null,
): JoinConflict | null {
  if (!payload) {
    return null;
  }
  const id = Number(payload.conflict_id);
  if (!Number.isFinite(id) || id <= 0) {
    return null;
  }
  return {
    id,
    ...(payload.conflict_name ? {name: payload.conflict_name} : {}),
  };
}
